import React, { useEffect } from 'react';
import { Search, X } from 'lucide-react';
import type { NavGroup } from '../data/navigation';

interface SidebarProps {
    sidebarOpen: boolean;
    setSidebarOpen: (open: boolean) => void;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    activeSection: string;
    scrollTo: (id: string) => void;
    filteredNav: NavGroup[];
}

export const Sidebar: React.FC<SidebarProps> = ({
    sidebarOpen,
    setSidebarOpen,
    searchQuery,
    setSearchQuery,
    activeSection,
    scrollTo,
    filteredNav
}) => {
    useEffect(() => {
        if (activeSection) {
            const activeElement = document.getElementById(`nav-item-${activeSection}`);
            if (activeElement) {
                activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            }
        }
    }, [activeSection]);

    return (
        <aside className={`fixed top-0 left-0 h-full w-[260px] bg-gray-900 border-r border-white/5 flex flex-col z-[100] transition-transform duration-300 shadow-2xl ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>

            {/* Header */}
            <div className="p-4 border-b border-white/5 bg-gradient-to-b from-orange-500/[0.03] to-transparent relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="flex items-center justify-between relative z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-[34px] h-[34px] rounded-lg flex items-center justify-center bg-gradient-to-br from-gray-800 to-[#0a0a0a] border border-white/10 shadow-[0_4px_10px_rgba(0,0,0,0.4)] overflow-hidden">
                            <img src="/owllogopng.png" alt="Logo" className="w-[85%] h-[85%] object-contain" />
                        </div>
                        <div>
                            <h1 className="font-extrabold text-[14px] text-gray-100 tracking-tight leading-tight">Ovul Scanner</h1>
                            <span className="text-[9px] text-orange-500/80 font-bold uppercase tracking-[0.2em]">Documentation</span>
                        </div>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden p-1.5 text-gray-400 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="px-4 py-3 border-b border-white/5">
                <div className="relative group">
                    <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-orange-500 transition-colors">
                        <Search size={14} />
                    </div>
                    <input
                        type="text"
                        placeholder="Search docs..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-[7px] bg-gray-900 border border-white/5 hover:border-white/10 rounded-lg text-[12px] text-gray-200 focus:outline-none focus:border-orange-500/50 focus:bg-gray-800  /50 transition-all placeholder:text-gray-600 shadow-inner"
                    />
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-3 space-y-4 pt-4 custom-scrollbar">
                {filteredNav.map((group) => (
                    <div key={group.group}>
                        <div className="flex items-center gap-2 mb-1.5 px-2">
                            <div className="h-[2px] flex-1 bg-gradient-to-r from-orange-500/40 to-transparent"></div>
                            <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-gray-200 whitespace-nowrap">{group.group}</div>
                        </div>
                        <div className="space-y-[2px]">
                            {group.items.map((item) => {
                                const isActive = activeSection === item.id;
                                const Icon = item.icon;
                                return (
                                    <button
                                        key={item.id}
                                        id={`nav-item-${item.id}`}
                                        onClick={() => {
                                            scrollTo(item.id);
                                            // Close sidebar on mobile after clicking
                                            if (window.innerWidth < 1024) setSidebarOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-2.5 px-3 py-[7px] rounded text-[12px] transition-all group ${isActive
                                            ? 'bg-gradient-to-r from-orange-500/20 to-transparent text-orange-400 font-semibold'
                                            : 'text-gray-200 hover:text-gray-200 hover:bg-white/[0.03] font-medium'
                                            }`}
                                    >
                                        <Icon size={14} className={`flex-shrink-0 transition-colors ${isActive ? 'text-orange-500' : item.color || 'text-gray-500 group-hover:text-gray-300'}`} />
                                        <span className="truncate">{item.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-white/5 bg-gray-950/30">
                <div className="flex items-center justify-center gap-2 py-2 bg-black/40 border border-white/5 rounded-lg text-[10px] font-bold text-gray-400 shadow-inner">
                    <span className="relative flex h-[6px] w-[6px]">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-[6px] w-[6px] bg-green-500"></span>
                    </span>
                    <span className="tracking-widest drop-shadow-sm">v1.0 — 2026</span>
                </div>
            </div>
        </aside>
    );
};
